/**
 * Quais etapas a configuração já define.
 *
 * Ao abrir um modelo pronto ou um miolo salvo para edição, o assistente começa
 * no passo 1 — e sem esta conta nada aparecia marcado, como se tudo estivesse
 * por fazer. Comparando com a configuração neutra, dá para mostrar o que aquele
 * miolo já traz resolvido e sobra atenção para o que falta decidir.
 *
 * A comparação é feita campo a campo, na mesma divisão dos passos do
 * assistente. Não é uma verdade absoluta (o modelo pode ter escolhido um valor
 * igual ao padrão), mas erra para menos: nunca marca como definido o que o
 * usuário ainda não viu.
 */

import { criarConfigPadrao, quadrosSugeridos, type ConfigPlanner } from '@miolos/core';

/** Compara objetos de configuração por valor. */
function mudou(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) !== JSON.stringify(b);
}

/** Algum dado pessoal foi preenchido? */
function temDadosPessoais(config: ConfigPlanner): boolean {
  const dados = config.dadosPessoais;
  return Object.values(dados).some((valor) => typeof valor === 'string' && valor.trim().length > 0);
}

export function passosJaDefinidos(config: ConfigPlanner): string[] {
  const padrao = criarConfigPadrao();
  const definidos: string[] = [];
  const marcar = (id: string, definido: boolean) => {
    if (definido) definidos.push(id);
  };

  marcar('formato', config.formato !== padrao.formato || mudou(config.papel, padrao.papel));
  marcar(
    'dias-por-pagina',
    config.paginasDeDia !== padrao.paginasDeDia ||
      config.diasPorPagina !== padrao.diasPorPagina ||
      // Ausente no miolo antigo e `false` no padrão são a mesma coisa.
      Boolean(config.fimDeSemanaDividido) !== Boolean(padrao.fimDeSemanaDividido),
  );
  marcar('semana', config.semana !== padrao.semana);
  marcar('cores', mudou(config.cores, padrao.cores));
  marcar('estilo', config.estilo !== padrao.estilo);
  marcar(
    'calendario',
    mudou(config.calendario, padrao.calendario) ||
      config.feriados !== padrao.feriados ||
      config.uf !== padrao.uf,
  );
  marcar('tema', mudou(config.ornamento, padrao.ornamento) || mudou(config.tipografia, padrao.tipografia));
  marcar('linhas', mudou(config.escrita, padrao.escrita));
  marcar(
    'imagem',
    config.camadasImagem.length > 0 || config.fundos.length > 0 || Boolean(config.capas?.frente),
  );
  marcar('dados-pessoais', temDadosPessoais(config));
  marcar('extras', mudou(config.paginasExtras, padrao.paginasExtras));
  marcar('cadernos', config.cadernos.length > 0);
  /*
   * Os quadros são comparados com a sugestão do estilo escolhido — trocar de
   * estilo já muda o conjunto esperado — e com o padrão de fábrica, que não
   * marca nenhum. Sem a segunda comparação, um miolo novo (nada marcado) seria
   * dado como resolvido no passo "Quadros do dia", que ninguém abriu.
   */
  marcar(
    'quadros',
    mudou(config.quadros, quadrosSugeridos(config.estilo)) &&
      mudou(config.quadros, padrao.quadros),
  );

  return definidos;
}
